/**
 * Collision graph builder — lays out 6 zones in a "two superclusters colliding"
 * spatial arrangement.
 *
 * Layout:
 *   Center:             Core Overlap + Shared Frontier intermingled as one cluster
 *   Inner left arc:     Incoming Signals (friend's artists connecting to yours)
 *   Inner right arc:    Outgoing Signals (your artists connecting to friend's)
 *   Outer left arc:     Your Artists (exclusive) — network scattered in left arc region
 *   Outer right arc:    Friend's Artists (exclusive) — network scattered in right arc region
 *
 * Both signal zones and exclusive artist zones use network-style force layouts
 * scattered across semi-circular arc regions. Signal arcs sit between the shared
 * central cluster and the exclusive artist arcs. The shared overlap and shared
 * frontier zones are placed together in a single force simulation so they
 * intermingle, with strong collision forces guaranteeing no two center-zone
 * nodes visually overlap.
 */

import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCenter,
  forceCollide,
  forceX,
  forceY,
} from 'd3-force';

// Zone color definitions
export const ZONE_COLORS = {
  core_overlap:       { h: 45,  s: 90, l: 65 },   // Bright gold
  your_artists:       { h: 230, s: 70, l: 60 },   // Blue/purple
  friend_artists:     { h: 15,  s: 75, l: 58 },   // Warm coral
  shared_frontier:    { h: 175, s: 70, l: 55 },   // Cyan/teal
  your_exploration:   { h: 280, s: 55, l: 60 },   // Purple-pink blend
  friend_exploration: { h: 30,  s: 60, l: 55 },   // Warm amber blend
};

// Layout constraints
const MAX_ZONE_RADIUS = 300;   // cap per-zone cluster radius to prevent unbounded growth
const INTRO_SLIDE = 400;       // how far left/right superclusters slide in during intro

const NODE_SIZE = {
  core: 8,
  member: 6,
  exploration: 5,
  frontier: 7,
};

/**
 * Run a simulation's tick loop with early termination.
 * Stops when total node movement per tick drops below `threshold`,
 * avoiding hundreds of ticks where nodes have already converged.
 */
function tickUntilStable(sim, nodes, maxTicks, threshold = 0.5) {
  for (let i = 0; i < maxTicks; i++) {
    sim.tick();
    // Check convergence every 10 ticks to avoid per-tick overhead
    if ((i + 1) % 10 === 0 && i > 30) {
      let totalMovement = 0;
      for (const n of nodes) {
        totalMovement += Math.abs(n.vx || 0) + Math.abs(n.vy || 0);
      }
      if (totalMovement < threshold) break;
    }
  }
}

/**
 * Returns the collision padding (gap added to each node's radius in the
 * collision force) for a cluster of `n` center-zone nodes. Scales up with
 * count so dense clusters have visible breathing room.
 */
function centerCollisionPad(n) {
  if (n > 150) return 14;
  if (n > 80)  return 12;
  if (n > 40)  return 9;
  if (n > 15)  return 7;
  return 6;
}

/**
 * Web-like force layout for the center cluster (shared overlap + shared
 * frontier). Uses weaker centering and stronger link forces so that link
 * topology drives the shape instead of charge + center creating concentric
 * rings. Nodes are given scattered initial positions to break symmetry.
 * Collision padding scales up with node count so dense clusters spread.
 */
function settleWebLayout(nodes, links) {
  if (nodes.length === 0) return;

  const n = nodes.length;

  // Scale parameters with node count so dense clusters spread out
  const scatter = Math.max(220, n * 5);
  const linkDist = n > 100 ? 85 : n > 60 ? 75 : n > 30 ? 65 : 55;
  const charge = n > 100 ? -220 : n > 60 ? -170 : n > 30 ? -130 : -95;
  const collisionPad = centerCollisionPad(n);
  const ticks = Math.min(450, 220 + n);

  // Scatter initial positions to break symmetry
  for (let i = 0; i < n; i++) {
    const h1 = Math.abs(Math.sin(i * 12.9898 + 78.233) * 43758.5453) % 1;
    const h2 = Math.abs(Math.sin(i * 78.233 + 12.9898) * 43758.5453) % 1;
    nodes[i].x = (h1 - 0.5) * scatter;
    nodes[i].y = (h2 - 0.5) * scatter;
  }

  const sim = forceSimulation(nodes)
    .force(
      'link',
      forceLink(links)
        .id((d) => d.id)
        .distance(linkDist)
        .strength(0.35)
    )
    .force('charge', forceManyBody().strength(charge))
    .force('center', forceCenter(0, 0).strength(0.025))
    .force('collision', forceCollide().radius((d) => (d.radius || 5) + collisionPad).iterations(3))
    .stop();

  tickUntilStable(sim, nodes, ticks);
}

/**
 * Scale down node positions proportionally if the cluster exceeds maxR,
 * then resolve any overlaps introduced by the scaling.
 */
function clampClusterRadius(nodes, maxR) {
  if (nodes.length === 0) return;
  let maxDist = 0;
  for (const n of nodes) {
    const d = Math.sqrt(n.x * n.x + n.y * n.y);
    if (d > maxDist) maxDist = d;
  }
  if (maxDist > maxR) {
    const scale = maxR / maxDist;
    for (const n of nodes) {
      n.x *= scale;
      n.y *= scale;
    }
    // Scaling can push nodes into overlapping positions — resolve collisions
    resolveOverlaps(nodes);
  }
}

/**
 * Run a short collision-only simulation to push apart any overlapping nodes.
 * Uses very weak centering so the cluster doesn't drift, but strong collision
 * forces so nodes separate cleanly.
 */
function resolveOverlaps(nodes) {
  if (nodes.length < 2) return;
  const sim = forceSimulation(nodes)
    .force('collision', forceCollide().radius((d) => (d.radius || 5) + 5).iterations(3))
    .force('center', forceCenter(0, 0).strength(0.01))
    .stop();
  tickUntilStable(sim, nodes, 40, 0.3);
}

/**
 * Network-style force layout scattered within a semi-circular arc region.
 * Nodes are given initial positions spread across the arc, with per-node
 * positional targets providing a gentle pull to maintain the arc shape.
 * Link and charge forces create organic network topology instead of
 * rigid concentric arc rows.
 */
function settleNetworkInArc(nodes, links, arcRadius, startAngle, endAngle, nodeRadius, opts = {}) {
  const n = nodes.length;
  if (n === 0) return;

  const arcSpan = endAngle - startAngle;
  const defaultBand = Math.max(100, Math.min(260, n * 3.5));
  const bandWidth = opts.maxBandWidth ? Math.min(defaultBand, opts.maxBandWidth) : defaultBand;

  // Compute how many nodes fit per radial ring at this arc radius
  const arcLen = arcRadius * Math.abs(arcSpan);
  const minSpacing = nodeRadius * 2 + 14;
  const perRing = Math.max(1, Math.floor(arcLen / minSpacing));
  const numRings = Math.max(1, Math.ceil(n / perRing));
  const ringStep = numRings > 1 ? bandWidth / (numRings - 1) : 0;

  // Shuffle node assignment order so linked neighbours don't cluster together
  const order = nodes.map((_, i) => i);
  for (let i = order.length - 1; i > 0; i--) {
    const h = Math.abs(Math.sin(i * 39.3468 + 27.1549) * 43758.5453) % 1;
    const j = Math.floor(h * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }

  // Stratified target placement: evenly distribute across rings and angles
  for (let slot = 0; slot < n; slot++) {
    const i = order[slot];
    const ring = slot % numRings;
    const posInRing = Math.floor(slot / numRings);
    const countInRing = Math.ceil((n - ring) / numRings);

    // Even angular spacing within this ring
    const t = countInRing === 1 ? 0.5 : posInRing / (countInRing - 1);
    const angle = startAngle + t * arcSpan;

    // Radial position: when outwardBias is set, shift rings so they spread
    // mostly outward from arcRadius rather than symmetrically around it.
    const biasShift = opts.outwardBias != null ? (0.5 - opts.outwardBias) * bandWidth * 0.5 : 0;
    const rOffset = numRings > 1 ? (ring - (numRings - 1) / 2) * ringStep : 0;
    const r = arcRadius + rOffset + biasShift;

    // Small hash-based jitter for organic feel
    const h1 = Math.abs(Math.sin(i * 12.9898 + 78.233) * 43758.5453) % 1;
    const h2 = Math.abs(Math.sin(i * 78.233 + 12.9898) * 43758.5453) % 1;
    const angJitter = (h1 - 0.5) * (arcSpan / Math.max(countInRing, 1)) * 0.25;
    const radJitter = (h2 - 0.5) * Math.max(ringStep * 0.3, 8);

    nodes[i]._tx = Math.cos(angle + angJitter) * (r + radJitter);
    nodes[i]._ty = Math.sin(angle + angJitter) * (r + radJitter);
    nodes[i].x = nodes[i]._tx + (h2 - 0.5) * 30;
    nodes[i].y = nodes[i]._ty + (h1 - 0.5) * 30;
  }

  const linkDist = n > 40 ? 80 : n > 20 ? 68 : 56;
  const charge = n > 40 ? -200 : n > 20 ? -150 : -100;
  const ticks = Math.min(300, 150 + n);

  // Soft containment boundary — replaces hard post-sim clamping so nodes
  // form an organic network that stays roughly within the arc region.
  // outwardBias controls asymmetric spread: lower values mean less inward
  // spread and more outward, keeping a clear gap on the inner side.
  const inwardRatio = opts.outwardBias != null ? opts.outwardBias : (opts.maxBandWidth ? 0.5 : 0.65);
  const outwardRatio = 1.0 - inwardRatio;
  const boundaryPush = opts.maxBandWidth ? 0.6 : (opts.outwardBias != null ? 0.7 : 0.4);
  const minR = arcRadius - bandWidth * inwardRatio;
  const maxR = arcRadius + bandWidth * outwardRatio;
  const midAngle = (startAngle + endAngle) / 2;
  const halfSpan = Math.abs(arcSpan) / 2;

  function boundaryForce(alpha) {
    for (const node of nodes) {
      const angle = Math.atan2(node.y, node.x);
      const r = Math.sqrt(node.x * node.x + node.y * node.y);

      // Soft radial containment — push back when outside the band.
      // The inward boundary (facing the neighbouring zone) uses a stronger
      // push when outwardBias is set, so nodes prefer to spread outward
      // into open space rather than encroaching on the gap.
      if (r > maxR) {
        const excess = (r - maxR) / r;
        node.vx -= node.x * excess * boundaryPush * alpha;
        node.vy -= node.y * excess * boundaryPush * alpha;
      } else if (r < minR && r > 0) {
        const inwardPush = opts.outwardBias != null ? boundaryPush * 2.5 : boundaryPush;
        const deficit = (minR - r) / r;
        node.vx += node.x * deficit * inwardPush * alpha;
        node.vy += node.y * deficit * inwardPush * alpha;
      }

      // Soft angular containment — nudge back when outside arc span
      let diff = angle - midAngle;
      while (diff > Math.PI) diff -= 2 * Math.PI;
      while (diff < -Math.PI) diff += 2 * Math.PI;
      if (Math.abs(diff) > halfSpan) {
        const targetAngle = diff > 0 ? midAngle + halfSpan : midAngle - halfSpan;
        const targetX = Math.cos(targetAngle) * r;
        const targetY = Math.sin(targetAngle) * r;
        node.vx += (targetX - node.x) * 0.15 * alpha;
        node.vy += (targetY - node.y) * 0.15 * alpha;
      }
    }
  }

  const sim = forceSimulation(nodes)
    .force(
      'link',
      forceLink(links)
        .id((d) => d.id)
        .distance(linkDist)
        .strength(0.35)
    )
    .force('charge', forceManyBody().strength(charge))
    .force('x', forceX((d) => d._tx).strength(0.03))
    .force('y', forceY((d) => d._ty).strength(0.03))
    .force('collision', forceCollide().radius(nodeRadius + 10).iterations(2))
    .force('boundary', boundaryForce)
    .stop();

  tickUntilStable(sim, nodes, ticks);

  // Clean up temporary target properties
  for (const node of nodes) {
    delete node._tx;
    delete node._ty;
  }
}


/**
 * Compute zone center positions for the inner zones.
 * Exploration zones are placed along the x-axis flanking the center.
 * Exclusive artist zones return (0,0) here — they use arc placement instead.
 *
 * Also returns `innerExtent`: the maximum distance from origin to any
 * inner zone edge, used to determine the semi-circle arc radius.
 */
function computeZoneCenters(zoneRadii) {
  // Only core overlap and shared frontier contribute to the inner extent.
  // Exploration zones and exclusive artist zones are placed in arc regions.
  const innerExtent = Math.max(zoneRadii.core_overlap || 40, zoneRadii.shared_frontier || 0);

  return {
    centers: {
      core_overlap:       { x: 0, y: 0 },
      shared_frontier:    { x: 0, y: 0 },
      your_exploration:   { x: 0, y: 0 },   // placeholder — arc placement overrides
      friend_exploration: { x: 0, y: 0 },   // placeholder — arc placement overrides
      your_artists:       { x: 0, y: 0 },   // placeholder — arc placement overrides
      friend_artists:     { x: 0, y: 0 },   // placeholder — arc placement overrides
    },
    innerExtent,
  };
}

/**
 * Compute intro animation offsets for a zone. Left-side zones slide in
 * from the left, right-side zones from the right, center zones fade in.
 */
function getIntroOffset(zoneKey) {
  switch (zoneKey) {
    case 'your_artists':
    case 'your_exploration':
      return { x: -INTRO_SLIDE, y: 0 };
    case 'friend_artists':
    case 'friend_exploration':
      return { x: INTRO_SLIDE, y: 0 };
    default:
      return { x: 0, y: 0 };
  }
}

/**
 * Build the full collision layout from server collision data.
 * Returns { allNodes, allLinks, zoneMetas, worldBounds }
 */
export function buildCollisionLayout(collisionData) {
  if (!collisionData || !collisionData.zones) {
    return { allNodes: [], allLinks: [], zoneMetas: [], worldBounds: { minX: 0, minY: 0, maxX: 0, maxY: 0 } };
  }

  const { zones, links: rawLinks } = collisionData;

  const zoneEntries = [
    { key: 'core_overlap',       data: zones.coreOverlap || [],       label: 'Core Overlap' },
    { key: 'your_artists',       data: zones.yourArtists || [],       label: 'Your Artists' },
    { key: 'friend_artists',     data: zones.friendArtists || [],     label: "Friend's Artists" },
    { key: 'shared_frontier',    data: zones.sharedFrontier || [],    label: 'Shared Frontier' },
    { key: 'your_exploration',   data: zones.yourExploration || [],   label: 'Incoming Signals' },
    { key: 'friend_exploration', data: zones.friendExploration || [], label: 'Outgoing Signals' },
  ];

  // Zones that use network scatter in arc regions (deferred placement)
  const ARC_ZONES = new Set(['your_artists', 'friend_artists']);
  const EXPLORATION_ARC_ZONES = new Set(['your_exploration', 'friend_exploration']);
  const ALL_ARC_ZONES = new Set([...ARC_ZONES, ...EXPLORATION_ARC_ZONES]);

  // Semi-circle angular parameters (radians).
  // The margin prevents the arc tips from reaching x=0, creating the
  // "almost meet at the midline" effect.
  const ARC_TIP_MARGIN = 0.04;  // ~2 degrees from the vertical axis
  // Left semi-circle: sweeps from near-top to near-bottom on the LEFT side
  const LEFT_ARC_START  = Math.PI / 2 + ARC_TIP_MARGIN;
  const LEFT_ARC_END    = 3 * Math.PI / 2 - ARC_TIP_MARGIN;
  // Right semi-circle: sweeps from near-bottom to near-top on the RIGHT side
  const RIGHT_ARC_START = -(Math.PI / 2) + ARC_TIP_MARGIN;
  const RIGHT_ARC_END   = Math.PI / 2 - ARC_TIP_MARGIN;

  // The shared overlap and shared frontier zones layout together as one
  // intermingled cluster centered at origin — see combined-layout pass below.
  const CENTER_ZONES = new Set(['core_overlap', 'shared_frontier']);

  // --- Pass 1: Create nodes, run simulations, compute visual radii (local coords) ---
  const nodeById = new Map();
  const nodeByName = new Map();
  const zoneResults = [];

  for (const entry of zoneEntries) {
    const color = ZONE_COLORS[entry.key];
    const zoneNodes = [];
    const zoneLinks = [];

    for (const artist of entry.data) {
      const isCore = entry.key === 'core_overlap';
      const isFrontier = entry.key === 'shared_frontier';
      const isExploration = entry.key === 'your_exploration' || entry.key === 'friend_exploration';

      const radius = isCore ? NODE_SIZE.core
        : isFrontier ? NODE_SIZE.frontier
        : isExploration ? NODE_SIZE.exploration
        : NODE_SIZE.member;

      const brightness = isCore ? 0.9 : isFrontier ? 0.6 : 0.75;

      const relatedSeedNames = isExploration
        ? (artist.connectedTo || [])
        : isFrontier
          ? (Array.isArray(artist.suggestedBy) ? artist.suggestedBy : artist.suggestedBy ? [artist.suggestedBy] : [])
          : [];

      const node = {
        id: `${entry.key}-${artist.name}`,
        name: artist.name,
        image: artist.image || null,
        zone: entry.key,
        zoneLabel: entry.label,
        type: isCore ? 'seed' : 'recommendation',
        radius,
        color: `hsla(${color.h}, ${color.s}%, ${color.l}%, ${brightness})`,
        glowColor: `hsla(${color.h}, ${color.s}%, ${color.l}%, ${brightness * 0.3})`,
        zoneColor: color,
        brightness,
        connectedTo: artist.connectedTo || [],
        suggestedBy: artist.suggestedBy || null,
        relatedSeedNames,
        score: artist.score || 0,
        compositeScore: isCore ? 1.0 : isFrontier ? (artist.score || 0.5) : 0.7,
      };

      zoneNodes.push(node);
      nodeById.set(node.id, node);
      if (!nodeByName.has(artist.name.toLowerCase().trim())) {
        nodeByName.set(artist.name.toLowerCase().trim(), node);
      }
    }

    // Intra-zone links for network structure
    if (zoneNodes.length > 1) {
      // Web-like connectivity: random spanning chain + cross-links.
      const order = zoneNodes.map((_, i) => i);
      for (let i = order.length - 1; i > 0; i--) {
        const h = Math.abs(Math.sin(i * 39.3468 + 27.1549) * 43758.5453) % 1;
        const j = Math.floor(h * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
      }
      for (let i = 0; i < order.length - 1; i++) {
        zoneLinks.push({
          source: zoneNodes[order[i]].id,
          target: zoneNodes[order[i + 1]].id,
          strength: 0.15,
          isIntraZone: true,
        });
      }
      const extraCount = Math.max(2, Math.floor(zoneNodes.length * 0.6));
      for (let k = 0; k < extraCount; k++) {
        const h1 = Math.abs(Math.sin(k * 53.1234 + 91.7654) * 43758.5453) % 1;
        const h2 = Math.abs(Math.sin(k * 91.7654 + 53.1234) * 43758.5453) % 1;
        const a = Math.floor(h1 * zoneNodes.length);
        let b = Math.floor(h2 * (zoneNodes.length - 1));
        if (b >= a) b++;
        zoneLinks.push({
          source: zoneNodes[a].id,
          target: zoneNodes[b].id,
          strength: 0.1,
          isIntraZone: true,
        });
      }
    }

    // Layout in local coordinates (centered at origin).
    // Arc zones and center zones (core overlap + shared frontier) are
    // deferred: arc zones wait for inner extent; center zones run together
    // in a single combined simulation so they intermingle.
    if (ALL_ARC_ZONES.has(entry.key) || CENTER_ZONES.has(entry.key)) {
      // No layout yet — deferred placement happens after the loop
    } else if (zoneNodes.length > 0) {
      settleWebLayout(zoneNodes, zoneLinks);
      clampClusterRadius(zoneNodes, MAX_ZONE_RADIUS);
    }

    // Compute visual radius (local coords, before world offset).
    // Arc and center zones get a placeholder — recomputed after their
    // deferred placement runs.
    let maxDist = 0;
    for (const n of zoneNodes) {
      const d = Math.sqrt(n.x * n.x + n.y * n.y);
      if (d + (n.radius || 5) > maxDist) maxDist = d + (n.radius || 5);
    }
    const visualRadius = (ALL_ARC_ZONES.has(entry.key) || CENTER_ZONES.has(entry.key))
      ? 40
      : Math.max(maxDist, 40);

    zoneResults.push({ entry, nodes: zoneNodes, links: zoneLinks, visualRadius });
  }

  // --- Combined layout for shared overlap + shared frontier ---
  // Both center zones run through a single force simulation so their nodes
  // intermingle as one cluster instead of frontier forming a separate shell
  // around the core. Temporary cross-zone attraction links from each frontier
  // node to its suggestedBy core node steer the simulation so frontier nodes
  // sit near the core artists they relate to. A final collision-only pass
  // guarantees no two center-zone nodes visually overlap.
  const coreZR = zoneResults.find(zr => zr.entry.key === 'core_overlap');
  const frontierZR = zoneResults.find(zr => zr.entry.key === 'shared_frontier');
  if (coreZR && frontierZR && (coreZR.nodes.length + frontierZR.nodes.length) > 0) {
    const crossLinks = [];
    for (const fNode of frontierZR.nodes) {
      for (const seedName of fNode.relatedSeedNames || []) {
        const seed = nodeByName.get(String(seedName).toLowerCase().trim());
        if (seed && seed.zone === 'core_overlap') {
          crossLinks.push({ source: fNode.id, target: seed.id, strength: 0.4 });
        }
      }
    }

    const combinedNodes = [...coreZR.nodes, ...frontierZR.nodes];
    const combinedLinks = [...coreZR.links, ...frontierZR.links, ...crossLinks];

    if (combinedNodes.length > 0) {
      settleWebLayout(combinedNodes, combinedLinks);

      if (combinedNodes.length > 1) {
        // Use the same breathing-room padding as the main simulation so this
        // pass guarantees clean separation without compressing the cluster.
        // We don't clamp the cluster radius here: as the shared-artist count
        // grows the center should expand naturally and push the surrounding
        // arc zones outward (innerExtent below picks up the larger radius).
        const finalPad = centerCollisionPad(combinedNodes.length);
        const finalPass = forceSimulation(combinedNodes)
          .force('collision', forceCollide().radius((d) => (d.radius || 5) + finalPad).iterations(8))
          .force('center', forceCenter(0, 0).strength(0.003))
          .stop();
        tickUntilStable(finalPass, combinedNodes, 100, 0.2);
      }

      for (const zr of [coreZR, frontierZR]) {
        let maxDist = 0;
        for (const n of zr.nodes) {
          const d = Math.sqrt(n.x * n.x + n.y * n.y);
          if (d + (n.radius || 5) > maxDist) maxDist = d + (n.radius || 5);
        }
        zr.visualRadius = Math.max(maxDist, 40);
      }
    }
  }

  // --- Compute dynamic zone centers for inner zones ---
  const zoneRadii = {};
  for (const zr of zoneResults) {
    zoneRadii[zr.entry.key] = zr.visualRadius;
  }
  const { centers: zoneCenters, innerExtent } = computeZoneCenters(zoneRadii);

  // --- Place exploration zones as scattered networks in inner arc region ---
  // The exploration band sits between the shared frontier and exclusive arcs.
  // Use a constrained band width so it doesn't bleed into adjacent layers.
  const EXPLORATION_MAX_BAND = 120;       // tighter band than exclusive arcs
  const EXPLORATION_INNER_GAP = 200;      // clear gap from shared frontier edge
  const EXPLORATION_OUTER_GAP = 120;      // clear gap before exclusive arcs
  const explorationArcRadius = innerExtent + EXPLORATION_INNER_GAP + EXPLORATION_MAX_BAND * 0.5;

  let explorationOuterExtent = innerExtent;

  for (const zr of zoneResults) {
    if (!EXPLORATION_ARC_ZONES.has(zr.entry.key) || zr.nodes.length === 0) continue;

    const isLeft = zr.entry.key === 'your_exploration';
    const startAngle = isLeft ? LEFT_ARC_START : RIGHT_ARC_START;
    const endAngle   = isLeft ? LEFT_ARC_END   : RIGHT_ARC_END;

    settleNetworkInArc(zr.nodes, zr.links, explorationArcRadius, startAngle, endAngle, NODE_SIZE.exploration, {
      maxBandWidth: EXPLORATION_MAX_BAND,
    });

    // Track the outermost node extent for placing exclusive arcs beyond
    for (const nd of zr.nodes) {
      const d = Math.sqrt(nd.x * nd.x + nd.y * nd.y) + (nd.radius || 5);
      if (d > explorationOuterExtent) explorationOuterExtent = d;
    }

    // Zone "center" for meta: use the centroid of placed nodes
    let cx = 0, cy = 0;
    for (const nd of zr.nodes) { cx += nd.x; cy += nd.y; }
    cx /= zr.nodes.length;
    cy /= zr.nodes.length;
    zoneCenters[zr.entry.key] = { x: cx, y: cy };

    // Compute visual radius relative to centroid
    let maxFromCenter = 0;
    for (const nd of zr.nodes) {
      const dx = nd.x - cx;
      const dy = nd.y - cy;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d + (nd.radius || 5) > maxFromCenter) maxFromCenter = d + (nd.radius || 5);
    }
    zr.visualRadius = Math.max(maxFromCenter, 40);
    zr._arcRadius = explorationArcRadius;
  }

  // --- Place exclusive artist zones as scattered networks in the outer arc region ---
  // The band is biased outward: instead of spreading symmetrically around the
  // arc radius, the inner edge sits at a fixed minimum distance from the
  // exploration outer extent so there is always a clear visual gap, while the
  // outer edge grows freely as artist count increases.
  let maxExclusiveCount = 0;
  for (const zr of zoneResults) {
    if (ARC_ZONES.has(zr.entry.key)) {
      maxExclusiveCount = Math.max(maxExclusiveCount, zr.nodes.length);
    }
  }
  const exclusiveBand = Math.max(100, Math.min(260, maxExclusiveCount * 3.5));
  // Scale the gap with artist count — more artists means the force sim pushes
  // nodes further, so we need a proportionally larger buffer to stay clear.
  const dynamicGap = EXPLORATION_OUTER_GAP + Math.max(0, maxExclusiveCount - 20) * 1.5;
  // The inner edge of the exclusive band is the hard floor: exploration outer
  // extent + dynamic gap.  The arc radius is placed so that the band spreads
  // mostly outward (30% inward, 70% outward) instead of 65/35.
  const EXCLUSIVE_INWARD_RATIO = 0.3;
  const exclusiveInwardSpread = exclusiveBand * EXCLUSIVE_INWARD_RATIO;
  const arcRadius = explorationOuterExtent + dynamicGap + exclusiveInwardSpread;

  for (const zr of zoneResults) {
    if (!ARC_ZONES.has(zr.entry.key) || zr.nodes.length === 0) continue;

    const isLeft = zr.entry.key === 'your_artists';
    const startAngle = isLeft ? LEFT_ARC_START : RIGHT_ARC_START;
    const endAngle   = isLeft ? LEFT_ARC_END   : RIGHT_ARC_END;

    settleNetworkInArc(zr.nodes, zr.links, arcRadius, startAngle, endAngle, NODE_SIZE.member, {
      // Tell settleNetworkInArc to bias the band outward
      outwardBias: EXCLUSIVE_INWARD_RATIO,
    });

    // Recompute visual radius from actual placed positions
    let maxDist = 0;
    for (const nd of zr.nodes) {
      const d = Math.sqrt(nd.x * nd.x + nd.y * nd.y);
      if (d + (nd.radius || 5) > maxDist) maxDist = d + (nd.radius || 5);
    }
    zr.visualRadius = Math.max(maxDist, 40);

    // Zone "center" for meta: use the centroid of placed nodes so hazes
    // and labels render near the densest part of the arc
    let cx = 0, cy = 0;
    for (const nd of zr.nodes) { cx += nd.x; cy += nd.y; }
    cx /= zr.nodes.length;
    cy /= zr.nodes.length;
    zoneCenters[zr.entry.key] = { x: cx, y: cy };

    // Recompute visual radius relative to centroid
    let maxFromCenter = 0;
    for (const nd of zr.nodes) {
      const dx = nd.x - cx;
      const dy = nd.y - cy;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d + (nd.radius || 5) > maxFromCenter) maxFromCenter = d + (nd.radius || 5);
    }
    zr.visualRadius = Math.max(maxFromCenter, 40);
  }

  // --- Pass 2: Offset to world positions, build output ---
  const allNodes = [];
  const allLinks = [];
  const zoneMetas = [];

  for (const zr of zoneResults) {
    const center = zoneCenters[zr.entry.key];
    const introOff = getIntroOffset(zr.entry.key);
    const isArc = ARC_ZONES.has(zr.entry.key);

    const isExplArc = EXPLORATION_ARC_ZONES.has(zr.entry.key);
    for (const n of zr.nodes) {
      if (!isArc && !isExplArc) {
        // Inner zones: offset from local coords to world coords
        n.x += center.x;
        n.y += center.y;
      }
      // Arc zones (both exclusive and exploration) are already in world coords
      n.homeX = n.x;
      n.homeY = n.y;
      const hash = Math.abs(Math.sin(n.x * 12.9898 + n.y * 78.233) * 43758.5453) % 1;
      n.driftRadius = 1 + hash * 2;
      n.driftSpeed = 0.0003 + hash * 0.0004;
      n.driftPhase = hash * Math.PI * 2;
      n.introOffsetX = introOff.x;
      n.introOffsetY = introOff.y;
    }

    allNodes.push(...zr.nodes);
    allLinks.push(...zr.links);

    const labelNames = zr.nodes.slice(0, 3).map((n) => n.name);

    zoneMetas.push({
      key: zr.entry.key,
      cx: center.x,
      cy: center.y,
      visualRadius: zr.visualRadius,
      label: zr.entry.label,
      color: ZONE_COLORS[zr.entry.key],
      count: zr.nodes.length,
      labelNames,
      introOffset: introOff,
      introFadeIn: zr.entry.key === 'core_overlap' || zr.entry.key === 'shared_frontier',
      isArc: isArc || isExplArc,
      arcRadius: isArc ? arcRadius : isExplArc ? zr._arcRadius : undefined,
    });
  }

  // Build inter-zone links from collision data
  if (rawLinks) {
    for (const link of rawLinks) {
      const sourceNode = nodeByName.get(link.source.toLowerCase().trim());
      const targetNode = nodeByName.get(link.target.toLowerCase().trim());
      if (!sourceNode || !targetNode) continue;

      const isCore = link.type === 'core';
      const isFrontier = link.type === 'frontier';
      const isExploration = link.type === 'exploration';

      allLinks.push({
        source: sourceNode,
        target: targetNode,
        strength: link.strength || 0.3,
        isCoreLink: isCore,
        isFrontierLink: isFrontier,
        isExplorationLink: isExploration,
        isCrossZone: sourceNode.zone !== targetNode.zone,
        opacity: isCore ? 0.15 : isFrontier ? 0.1 : 0.12,
      });
    }
  }

  // World bounds
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const n of allNodes) {
    const r = n.radius || 5;
    if (n.x - r < minX) minX = n.x - r;
    if (n.y - r < minY) minY = n.y - r;
    if (n.x + r > maxX) maxX = n.x + r;
    if (n.y + r > maxY) maxY = n.y + r;
  }
  for (const zm of zoneMetas) {
    if (zm.cx - zm.visualRadius < minX) minX = zm.cx - zm.visualRadius;
    if (zm.cy - zm.visualRadius < minY) minY = zm.cy - zm.visualRadius;
    if (zm.cx + zm.visualRadius > maxX) maxX = zm.cx + zm.visualRadius;
    if (zm.cy + zm.visualRadius > maxY) maxY = zm.cy + zm.visualRadius;
  }
  if (!isFinite(minX)) { minX = -500; minY = -300; maxX = 500; maxY = 300; }

  return { allNodes, allLinks, zoneMetas, worldBounds: { minX, minY, maxX, maxY } };
}
